import { cva } from 'class-variance-authority';
import React from 'react';
import Icon from '../Icon/icon';
import Spinner from '../Spinner/spinner';

import { clsxMerge } from '../../util';
import { IconNames } from '../../util/types';
import { onMouseEventHandler } from '../../util/eventHandlers';

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant: 'default' | 'small';
  iconName: IconNames;
  iconSize?: 'small' | 'medium';
  label?: string;
  disabled: boolean;
  className?: string;
  onClick: React.MouseEventHandler<HTMLButtonElement | HTMLInputElement>;
  hasIndicator?: boolean;
  hasTooltip?: boolean;
  iconColor?: string;
  isTransparent?: boolean;
  stateful?: boolean;
}

// Note that on button pressed bg and border should be the same color as per figma designs
// However when we use two same colors that are using opacity, the color for the border becomes darker than bg
// That is why we need to use a completely transparent color for the border to have it look the same as bg

const iconButton = cva(
  [
    'preflight',
    '!ds-font-sans',
    'ds-rounded',
    'ds-text-base',
    'ds-font-semibold',
    'ds-border',
    'ds-transition',
    'ds-duration-100',
    'ds-text-nowrap',
    'focus-visible:ds-border',
    'focus-visible:ds-ring',
    'focus-visible:ds-ring-2',
    'focus-visible:ds-outline-none',
    'focus-visible:ds-ring-offset-2'
  ],
  {
    variants: {
      variant: {
        default: ['ds-p-[10px]', 'ds-relative', 'ds-group'],
        small: ['ds-p-1']
      },
      isTransparent: {
        false: '',
        true: ''
      },
      disabled: {
        false: [
          'ds-text-neutrals-black',
          'hover:ds-bg-neutrals-transparent-light',
          'hover:ds-border-neutrals-transparent',
          'active:ds-bg-neutrals-transparent-medium',
          'active:ds-border-neutrals-transparent',
          'focus-visible:ds-bg-neutrals-transparent-light',
          'focus-visible:ds-border-neutrals-dark',
          'focus-visible:ds-ring-neutrals-black'
        ],
        true: [
          'ds-bg-neutrals-transparent-light',
          'ds-text-neutrals-dark',
          'ds-border-neutrals-transparent',
          'ds-cursor-not-allowed',
          'focus-visible:ds-border-neutrals-light',
          'focus-visible:ds-ring-neutrals-black'
        ]
      }
    },
    compoundVariants: [
      {
        isTransparent: true,
        disabled: false,
        className: ['ds-bg-neutrals-transparent', 'ds-border-neutrals-transparent']
      },
      {
        isTransparent: false,
        disabled: false,
        className: ['ds-bg-neutrals-white', 'ds-border-neutrals-white']
      }
    ]
  }
);

const IconButton: React.FC<IconButtonProps> = ({
  variant = 'default',
  label = '',
  disabled = false,
  className = '',
  onClick,
  iconName,
  iconSize = 'medium',
  hasIndicator = false,
  hasTooltip = true,
  isTransparent = true,
  iconColor = '',
  stateful = false,
  ...props
}) => {
  const isDisabled = stateful || disabled;
  return (
    <button
      onClick={(e) => onMouseEventHandler(e, isDisabled, onClick)}
      aria-disabled={isDisabled}
      aria-label={label}
      aria-pressed={stateful}
      aria-live={stateful ? 'assertive' : 'off'}
      className={clsxMerge(
        iconButton({ variant, isTransparent, disabled: isDisabled }),
        className
      )}
      {...props}
    >
      {stateful && variant === 'default' ? (
        <Spinner size="medium" focusable={false} label={label} ariaLive="off" />
      ) : (
        <Icon
          size={variant === 'small' ? 'small' : iconSize}
          name={iconName ?? 'edit'}
          hasIndicator={hasIndicator}
          className={
            disabled
              ? 'ds-text-neutrals-dark'
              : iconColor && iconColor !== ''
                ? iconColor
                : 'ds-text-neutrals-black'
          }
          altText={label}
        />
      )}

      {variant === 'default' && hasTooltip && (
        <div
          aria-hidden="true"
          className={`${!isDisabled ? 'group-hover:ds-block group-active:ds-block' : 'group-hover:ds-hidden'} ds-absolute ds-left-1/2 ds-top-[3.5rem] ds-z-10 ds-hidden ds--translate-x-1/2 ds-cursor-default ds-text-nowrap ds-rounded ds-bg-neutrals-darker ds-px-2 ds-py-1 ds-text-sm ds-font-normal ds-text-neutrals-white ds-transition-all ds-duration-100 group-focus-visible:ds-block`}
        >
          {label}
        </div>
      )}
    </button>
  );
};

export default IconButton;
